import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Calendar, Globe, Users, Settings, LayoutGrid } from 'lucide-react';

const stats = [
  { value: '7+', label: 'Years of Combined Leadership Experience', icon: Calendar },
  { value: '5', label: 'Countries of Planned Expansion', icon: Globe },
  { value: '', label: 'Access to a Qualified Specialist Network', icon: Users },
];

const competencies = [
  {
    title: 'INDUSTRIAL AUTOMATION',
    desc: 'PLC programming, robot programming, commissioning, and installation for advanced manufacturing environments.',
    image: '/about-automation.webp',
    icon: Settings,
  },
  {
    title: 'SYSTEMS INTEGRATION',
    desc: 'Multi-vendor architecture design, control system integration, and line optimization.',
    image: '/about-integration.webp',
    icon: LayoutGrid,
  },
  {
    title: 'GLOBAL DEPLOYMENT',
    desc: 'Precision workforce deployment of Red Seal qualified specialists to leading industrial facilities worldwide.',
    image: '/about-deployment.webp',
    icon: Globe,
  },
];

export default function About() {
  useDocumentTitle('About Us — Straton Group');

  return (
    <main id="main-content">
      {/* ── Hero Section ── */}
      <section className="relative min-h-[60vh] lg:min-h-[70vh] flex items-end">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="/about-hero.webp" fetchPriority="high"
            alt="Industrial automation facility"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-bg-primary via-bg-primary/80 to-bg-primary/40" />
        </div>

        {/* Text content */}
        <div className="relative z-10 w-full max-w-[1400px] mx-auto section-padding pt-24 lg:pt-32 pb-16 lg:pb-24">
          {/* Label with line */}
          <div className="flex items-center gap-4 mb-6">
            <div className="w-8 h-px bg-text-muted" />
            <span className="text-xs uppercase tracking-[0.2em] text-text-muted">
              About Straton
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-[0.15em] text-text-primary mb-6 leading-tight">
            We Engineer<br />
            Precision At Scale.
          </h1>

          <div className="w-8 h-px bg-text-muted mb-6" />

          <p className="text-sm uppercase tracking-[0.15em] text-text-secondary mb-6 max-w-xl">
            Industrial automation specialists operating at global scale.
          </p>

          <p className="body-text max-w-lg">
            Straton Group (PTY) Ltd is a technical services company specializing in industrial automation, systems integration, and the deployment of qualified engineering specialists. Headquartered in South Africa, we are building a global footprint with a focus on precision, reliability, and the people who make industrial environments run.
          </p>
        </div>
      </section>

      {/* ── Stats Section ── */}
      <section className="bg-bg-primary section-padding py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="border border-border-secondary">
            <div className="grid grid-cols-1 md:grid-cols-3">
              {stats.map((stat, i) => (
                <div
                  key={stat.label}
                  className={`flex items-center gap-6 py-10 lg:py-12 px-8 lg:px-12 ${
                    i < stats.length - 1
                      ? 'border-b md:border-b-0 md:border-r border-border-secondary'
                      : ''
                  }`}
                >
                  <stat.icon
                    className="w-6 h-6 text-text-muted shrink-0"
                    strokeWidth={1.2}
                  />
                  <div>
                    <span className="text-3xl md:text-4xl font-light text-text-primary tracking-wider block mb-1">
                      {stat.value}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-text-muted">
                      {stat.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Core Competencies ── */}
      <section className="bg-bg-primary section-padding pb-20">
        <div className="max-w-[1400px] mx-auto">
          {/* Label with line */}
          <div className="flex items-center gap-4 mb-6">
            <div className="w-8 h-px bg-text-muted" />
            <span className="text-xs uppercase tracking-[0.2em] text-text-muted">
              What We Do
            </span>
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold uppercase tracking-[0.15em] text-text-primary mb-10">
            Core Competencies
          </h2>

          <div className="border border-border-secondary grid grid-cols-1 md:grid-cols-3">
            {competencies.map((comp, i) => (
              <div
                key={comp.title}
                className={`${
                  i < 2
                    ? 'border-b md:border-b-0 md:border-r border-border-secondary'
                    : ''
                }`}
              >
                {/* Image with icon overlay */}
                <div className="relative h-64 lg:h-80 overflow-hidden">
                  <img
                    src={comp.image}
                    alt={comp.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-bg-primary/80 via-bg-primary/20 to-transparent" />
                  {/* Icon box */}
                  <div className="absolute bottom-4 left-4 w-10 h-10 border border-border-primary flex items-center justify-center bg-bg-primary/80">
                    <comp.icon
                      className="w-5 h-5 text-text-muted"
                      strokeWidth={1.2}
                    />
                  </div>
                </div>

                {/* Text */}
                <div className="p-6 lg:p-8">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.1em] text-text-primary mb-3">
                    {comp.title}
                  </h3>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    {comp.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Vision / Quote ── */}
      <section className="bg-bg-primary section-padding pb-20">
        <div className="max-w-[1400px] mx-auto">
          <div className="border border-border-secondary grid grid-cols-1 lg:grid-cols-2">
            {/* Left — Quote */}
            <div className="p-10 lg:p-16 flex flex-col justify-center order-2 lg:order-1">
              {/* Quote mark */}
              <svg
                className="w-8 h-8 text-text-muted mb-6"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
              </svg>

              <blockquote className="body-text text-text-primary leading-relaxed mb-8">
                To be the most trusted technical partner in industrial automation, known for precision, reliability, and the ability to operate at scale anywhere in the world.
              </blockquote>

              <div className="w-8 h-px bg-text-muted mb-6" />

              <cite className="not-italic">
                <span className="text-[10px] uppercase tracking-[0.15em] text-text-muted block">
                  Straton Group Operations Directive
                </span>
              </cite>
            </div>

            {/* Right — Image */}
            <div className="relative min-h-[300px] lg:min-h-[400px] order-1 lg:order-2">
              <img
                src="/about-bottom.webp" loading="lazy"
                alt="Global operations"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-bg-primary via-bg-primary/40 to-transparent" />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
